/**
 * Routes — Route definitions with lazy loading and protected routes.
 * F9 update: Goals and Sharing pages now fully implemented.
 * issue_20: Added DeckSourcePage for full-screen source viewing.
 * issue_55: Added SharedDeckPage for public share token access (/shared/:token).
 * DeckListDetailPage: Added /decks/collections/:id for collection detail view.
 * issue_75: Added LessonViewerPage for lesson viewing (/decks/:deckId/lessons/:lessonId).
 * issue_86: Added LegalTrackingPage for legal tracking (/decks/:deckId/legal-tracking
 *           and /decks/collections/:deckListId/legal-tracking).
 * issue_137: Added organization routes (register, join, dashboard, members, catalog, enrolls).
 * issue_138: Added member experience routes (my-courses, my-course detail, my-progress).
 * issue_139: Protected /import route with RoleGuard for RBAC.
 * issue_09: Added Stripe payment result pages (success, cancel).
 * issue_145: Added CourseDetailPage for course detail view (/organization/catalog/:deckListId).
 * issue_159: Changed /organization/purchases to /organization/enrolls.
 * issue_161: Added DashboardRoute wrapper to redirect ORGANIZATION_ADMIN from index to /organization.
 * issue_02: Split /organization/members into /organization/enrollments (OrganizationMembersPage)
 *           and /organization/users (OrganizationUsersPage). Old URL redirected for backwards compat.
 * issue_163: Added /onboarding route, RoleGuard on all content routes for CREATOR/ADMIN,
 *            DashboardRoute redirects USER (pending) to /onboarding.
 * issue_168: Added RegisterPage for public user registration (/register).
 * issue_169: Added ORGANIZATION_MEMBER to allowedRoles for LessonViewerPage, StudySessionPage,
 *            StudyCompletePage, and SessionReviewPage routes.
 * issue_174: Added MfaVerifyPage for TOTP challenge during login (/mfa-verify).
 * issue_175: Added ORGANIZATION_ADMIN to allowedRoles for LessonViewerPage, StudySessionPage,
 *            StudyCompletePage, and SessionReviewPage routes so ORGANIZATION_ADMIN is not
 *            redirected to /onboarding by RoleGuard.
 * issue_02_p0: Added public marketing pages (Landing, Solutions, Security, Privacy, Terms)
 *              wrapped in PublicLayout. Index route (/) now redirects to /landing for
 *              unauthenticated users, preserving DashboardRoute for authenticated users.
 * issue_04: Added AuditTrailPage for organization audit trail (/organization/audit-trail).
 * issue_05: Added ComplianceDashboardPage for organization compliance (/organization/compliance).
 * issue_191: Added CourseTestPage for course certification test (/my-courses/:deckListId/test).
 * issue_215: Removed IndexRoute and public "/" route. Index route is now inside the protected
 *            layout. Added ORGANIZATION_MEMBER redirect in DashboardRoute to /my-courses.
 * issue_217: Added assessment routes for CREATOR/ADMIN (list, editor) and ORGANIZATION_ADMIN
 *            (execution, report, history, non-conformities).
 *
 * P0.2 — Page Suspense fallback uses skeleton placeholder instead of
 *        spinner to reduce CLS during route transitions.
 */

import { lazy, Suspense } from 'react';

import { Routes, Route, Navigate } from 'react-router-dom';

import { ProtectedRoute } from '@/features/auth/auth.guard';
import { RoleGuard } from '@/features/auth/RoleGuard';
import { useAuth } from '@/features/auth/auth.hook';
import { UserRole } from '@flashcard/shared';

import { AppLayout } from '@/components/layout/AppLayout';
import { PublicLayout } from '@/components/layout/PublicLayout';

import { Skeleton } from '@/components/ui/Skeleton';

// ============================================================
// Lazy-loaded pages
// ============================================================
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const AuthCallbackPage = lazy(() => import('@/pages/AuthCallbackPage'));
// issue_168: Register page for public user registration
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
// issue_174: MFA verify page for TOTP challenge during login
const MfaVerifyPage = lazy(() => import('@/pages/MfaVerifyPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// issue_163: Onboarding page for USER (pending) role
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'));

// issue_02_p0: Public marketing pages
const LandingPage = lazy(() => import('@/pages/public/LandingPage'));
const SolutionsPage = lazy(() => import('@/pages/public/SolutionsPage'));
const SecurityTrustPage = lazy(() => import('@/pages/public/SecurityTrustPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/public/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('@/pages/public/TermsPage'));

// F7: Decks, Flashcard Manager, Import
const DecksPage = lazy(() => import('@/pages/DecksPage'));
const DeckDetailPage = lazy(() => import('@/pages/DeckDetailPage'));
const ImportPage = lazy(() => import('@/features/import/ImportPage'));

// DeckList (Collection) detail page
const DeckListDetailPage = lazy(() => import('@/pages/DeckListDetailPage'));

// F8: Study, Study Session, Study Complete
const StudyPage = lazy(() => import('@/pages/StudyPage'));
const StudySessionPage = lazy(() => import('@/pages/StudySessionPage'));
const StudyCompletePage = lazy(() => import('@/pages/StudyCompletePage'));

// issue_67: Session Review page — review session details with rating filter
const SessionReviewPage = lazy(() => import('@/pages/SessionReviewPage'));

// F9: Goals, Sharing (fully implemented)
const GoalsPage = lazy(() => import('@/pages/GoalsPage'));
const SharingPage = lazy(() => import('@/pages/SharingPage'));

// issue_55: Shared deck page (public, no auth required)
const SharedDeckPage = lazy(() => import('@/features/sharing/pages/SharedDeckPage'));

// issue_20: Deck Source full-screen view
const DeckSourcePage = lazy(() => import('@/pages/DeckSourcePage'));

// issue_75: Lesson viewer page (must be before /decks/:id to avoid route conflict)
const LessonViewerPage = lazy(() => import('@/pages/LessonViewerPage'));

// issue_86: Legal tracking page (lazy-loaded)
const LegalTrackingPage = lazy(() => import('@/pages/LegalTrackingPage'));

// issue_137: Organization pages
const RegisterOrganizationPage = lazy(() => import('@/pages/RegisterOrganizationPage'));
const JoinOrganizationPage = lazy(() => import('@/pages/JoinOrganizationPage'));
const OrganizationDashboardPage = lazy(() => import('@/pages/OrganizationDashboardPage'));
const OrganizationMembersPage = lazy(() => import('@/pages/OrganizationMembersPage'));
const OrganizationUsersPage = lazy(() => import('@/pages/OrganizationUsersPage'));
const OrganizationMemberProgressPage = lazy(() => import('@/pages/OrganizationMemberProgressPage'));
const CourseCatalogPage = lazy(() => import('@/pages/CourseCatalogPage'));
const CourseDetailPage = lazy(() => import('@/pages/CourseDetailPage'));
const OrganizationPurchasesPage = lazy(() => import('@/pages/OrganizationPurchasesPage'));

// issue_04: Audit Trail page
const AuditTrailPage = lazy(() => import('@/pages/AuditTrailPage'));

// issue_05: Compliance Dashboard page
const ComplianceDashboardPage = lazy(() => import('@/pages/ComplianceDashboardPage'));

// issue_138: Organization member pages
const MyCoursesPage = lazy(() => import('@/pages/MyCoursesPage'));
const MyCourseDetailPage = lazy(() => import('@/pages/MyCourseDetailPage'));
const MyProgressPage = lazy(() => import('@/pages/MyProgressPage'));

// issue_191: Course Test page (certification test execution)
const CourseTestPage = lazy(() => import('@/pages/CourseTestPage'));

// issue_09: Stripe payment pages
const StripeSuccessPage = lazy(() => import('@/pages/StripeSuccessPage'));
const StripeCancelPage = lazy(() => import('@/pages/StripeCancelPage'));

// issue_205: Admin Telemetry management page
const AdminTelemetryPage = lazy(() => import('@/pages/AdminTelemetryPage'));

// issue_217: Assessment pages
const AssessmentListPage = lazy(() => import('@/pages/AssessmentListPage'));
const AssessmentEditorPage = lazy(() => import('@/pages/AssessmentEditorPage'));
const AssessmentExecutionPage = lazy(() => import('@/pages/AssessmentExecutionPage'));
const AssessmentReportPage = lazy(() => import('@/pages/AssessmentReportPage'));
const NonConformityListPage = lazy(() => import('@/pages/NonConformityListPage'));

// ============================================================
// Suspense fallback — P0.2: Skeleton placeholder (fixed dimensions)
// ============================================================
function PageLoader() {
  return (
    <div className="mx-auto px-4 py-6 lg:px-8 max-w-full" style={{ minHeight: '60vh' }}>
      <div className="space-y-4">
        <Skeleton width="40%" height={32} rounded="lg" />
        <Skeleton width="60%" height={20} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6">
          <Skeleton height={120} rounded="lg" />
          <Skeleton height={120} rounded="lg" />
          <Skeleton height={120} rounded="lg" />
          <Skeleton height={120} rounded="lg" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <Skeleton height={320} rounded="lg" />
          <Skeleton height={320} rounded="lg" />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DashboardRoute — issue_161, issue_163, issue_215
// Redirects ORGANIZATION_ADMIN users from the index route (/)
// to /organization instead of showing the personal DashboardPage.
// issue_163: Redirects USER (pending) to /onboarding.
// issue_215: Redirects ORGANIZATION_MEMBER to /my-courses.
// ============================================================
function DashboardRoute() {
  const { user } = useAuth();

  if (user?.role === UserRole.ORGANIZATION_ADMIN) {
    return <Navigate to="/organization" replace />;
  }

  if (user?.role === UserRole.ORGANIZATION_MEMBER) {
    return <Navigate to="/my-courses" replace />;
  }

  if (user?.role === UserRole.USER) {
    return <Navigate to="/onboarding" replace />;
  }

  return <DashboardPage />;
}

// ============================================================
// App Routes
// ============================================================
export function AppRoutes() {
  return (
    <Routes>
      {/* ============================================================
          Public routes — no auth required
      ============================================================ */}

      {/* issue_02_p0: Public marketing pages wrapped in PublicLayout */}
      <Route path="/landing" element={
        <Suspense fallback={<PageLoader />}>
          <PublicLayout><LandingPage /></PublicLayout>
        </Suspense>
      } />
      <Route path="/solutions" element={
        <Suspense fallback={<PageLoader />}>
          <PublicLayout><SolutionsPage /></PublicLayout>
        </Suspense>
      } />
      <Route path="/security" element={
        <Suspense fallback={<PageLoader />}>
          <PublicLayout><SecurityTrustPage /></PublicLayout>
        </Suspense>
      } />
      <Route path="/privacy" element={
        <Suspense fallback={<PageLoader />}>
          <PublicLayout><PrivacyPolicyPage /></PublicLayout>
        </Suspense>
      } />
      <Route path="/terms" element={
        <Suspense fallback={<PageLoader />}>
          <PublicLayout><TermsPage /></PublicLayout>
        </Suspense>
      } />

      {/* Auth and public utility routes */}
      <Route path="/login" element={<Suspense fallback={<PageLoader />}><LoginPage /></Suspense>} />
      <Route path="/auth/callback" element={<Suspense fallback={<PageLoader />}><AuthCallbackPage /></Suspense>} />
      {/* issue_168: Register page — public, no auth required */}
      <Route path="/register" element={<Suspense fallback={<PageLoader />}><RegisterPage /></Suspense>} />
      {/* issue_174: MFA verify page — public, no auth required (TOTP challenge after Keycloak login) */}
      <Route path="/mfa-verify" element={<Suspense fallback={<PageLoader />}><MfaVerifyPage /></Suspense>} />

      {/* issue_55: Shared deck page — public, no auth required */}
      <Route path="/shared/:token" element={<Suspense fallback={<PageLoader />}><SharedDeckPage /></Suspense>} />

      {/* ============================================================
          Protected routes (wrapped in AppLayout)
      ============================================================ */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* issue_215: Index route — redirects based on role via DashboardRoute */}
        <Route index element={<DashboardRoute />} />

        {/* issue_163: Onboarding page — accessible to USER (pending) */}
        <Route path="/onboarding" element={<Suspense fallback={<PageLoader />}><OnboardingPage /></Suspense>} />

        {/* ============================================================
            F7: Decks & Flashcards
            issue_163: Protected with RoleGuard — USER (pending) redirected to /onboarding
        ============================================================ */}
        <Route path="/decks" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><DecksPage /></Suspense>
          </RoleGuard>
        } />
        {/* DeckListDetailPage MUST be before /decks/:id to avoid route conflict */}
        <Route path="/decks/collections/:id" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><DeckListDetailPage /></Suspense>
          </RoleGuard>
        } />
        {/* issue_86: Legal tracking for collection — MUST be before /decks/:id to avoid route conflict */}
        <Route path="/decks/collections/:deckListId/legal-tracking" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><LegalTrackingPage /></Suspense>
          </RoleGuard>
        } />
        {/* issue_75: LessonViewerPage MUST be before /decks/:id to avoid route conflict */}
        {/* issue_169: Added ORGANIZATION_MEMBER to allowedRoles */}
        {/* issue_175: Added ORGANIZATION_ADMIN to allowedRoles */}
        <Route path="/decks/:deckId/lessons/:lessonId" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN, UserRole.ORGANIZATION_ADMIN, UserRole.ORGANIZATION_MEMBER]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><LessonViewerPage /></Suspense>
          </RoleGuard>
        } />
        {/* issue_86: Legal tracking for single deck — MUST be before /decks/:id to avoid route conflict */}
        <Route path="/decks/:deckId/legal-tracking" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><LegalTrackingPage /></Suspense>
          </RoleGuard>
        } />
        <Route path="/decks/:id" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><DeckDetailPage /></Suspense>
          </RoleGuard>
        } />
        {/* issue_20: Deck Source full-screen view */}
        <Route path="/decks/:id/sources/:sourceId" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><DeckSourcePage /></Suspense>
          </RoleGuard>
        } />

        {/* F7: Import — issue_139: Protected with RoleGuard for RBAC
            issue_163: Changed allowedRoles from [USER, ADMIN] to [CREATOR, ADMIN] */}
        <Route path="/import" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><ImportPage /></Suspense>
          </RoleGuard>
        } />

        {/* ============================================================
            F8: Study
            issue_163: Protected with RoleGuard — USER (pending) redirected to /onboarding
            issue_169: Added ORGANIZATION_MEMBER to allowedRoles for session,
                      complete, and review routes.
            issue_175: Added ORGANIZATION_ADMIN to allowedRoles for session,
                      complete, and review routes.
        ============================================================ */}
        <Route path="/study" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><StudyPage /></Suspense>
          </RoleGuard>
        } />
        {/* issue_169: Added ORGANIZATION_MEMBER to allowedRoles */}
        {/* issue_175: Added ORGANIZATION_ADMIN to allowedRoles */}
        <Route path="/study/session/:id" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN, UserRole.ORGANIZATION_ADMIN, UserRole.ORGANIZATION_MEMBER]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><StudySessionPage /></Suspense>
          </RoleGuard>
        } />
        {/* issue_67: Session Review — review session details with rating filter */}
        {/* issue_169: Added ORGANIZATION_MEMBER to allowedRoles */}
        {/* issue_175: Added ORGANIZATION_ADMIN to allowedRoles */}
        <Route path="/study/review/:id" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN, UserRole.ORGANIZATION_ADMIN, UserRole.ORGANIZATION_MEMBER]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><SessionReviewPage /></Suspense>
          </RoleGuard>
        } />
        {/* issue_169: Added ORGANIZATION_MEMBER to allowedRoles */}
        {/* issue_175: Added ORGANIZATION_ADMIN to allowedRoles */}
        <Route path="/study/complete/:id" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN, UserRole.ORGANIZATION_ADMIN, UserRole.ORGANIZATION_MEMBER]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><StudyCompletePage /></Suspense>
          </RoleGuard>
        } />

        {/* ============================================================
            F9: Goals & Sharing
            issue_163: Protected with RoleGuard — USER (pending) redirected to /onboarding
        ============================================================ */}
        <Route path="/goals" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><GoalsPage /></Suspense>
          </RoleGuard>
        } />
        <Route path="/sharing" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><SharingPage /></Suspense>
          </RoleGuard>
        } />

        <Route path="/settings" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />

        {/* issue_205: Admin Telemetry management page — ADMIN only */}
        <Route path="/admin/telemetry" element={
          <RoleGuard allowedRoles={[UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><AdminTelemetryPage /></Suspense>
          </RoleGuard>
        } />

        {/* ============================================================
            issue_217: Assessment routes — CREATOR/ADMIN
            Routes for managing compliance assessments (list, create, edit).
        ============================================================ */}
        <Route path="/assessments" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><AssessmentListPage /></Suspense>
          </RoleGuard>
        } />
        <Route path="/assessments/new" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><AssessmentEditorPage /></Suspense>
          </RoleGuard>
        } />
        <Route path="/assessments/:id/edit" element={
          <RoleGuard allowedRoles={[UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><AssessmentEditorPage /></Suspense>
          </RoleGuard>
        } />

        {/* ============================================================
            issue_137: Organization routes
            Static routes first, then dynamic ones to avoid conflicts.
        ============================================================ */}
        {/* Public organization routes (accessible to all authenticated users) */}
        <Route path="/organization/register" element={<Suspense fallback={<PageLoader />}><RegisterOrganizationPage /></Suspense>} />
        <Route path="/organization/join" element={<Suspense fallback={<PageLoader />}><JoinOrganizationPage /></Suspense>} />
        {/* Organization admin routes (role check is done within each page) */}
        <Route path="/organization" element={<Suspense fallback={<PageLoader />}><OrganizationDashboardPage /></Suspense>} />
        {/* issue_02: Split /organization/members into /organization/enrollments and /organization/users */}
        <Route path="/organization/enrollments" element={<Suspense fallback={<PageLoader />}><OrganizationMembersPage /></Suspense>} />
        <Route path="/organization/users" element={<Suspense fallback={<PageLoader />}><OrganizationUsersPage /></Suspense>} />
        {/* Redirect old URL to new one for backwards compatibility */}
        <Route path="/organization/members" element={<Navigate to="/organization/enrollments" replace />} />
        <Route path="/organization/members/:memberId/progress" element={<Suspense fallback={<PageLoader />}><OrganizationMemberProgressPage /></Suspense>} />
        <Route path="/organization/catalog" element={<Suspense fallback={<PageLoader />}><CourseCatalogPage /></Suspense>} />
        <Route path="/organization/catalog/:deckListId" element={<Suspense fallback={<PageLoader />}><CourseDetailPage /></Suspense>} />
        {/* issue_159: Changed /organization/purchases to /organization/enrolls */}
        <Route path="/organization/enrolls" element={<Suspense fallback={<PageLoader />}><OrganizationPurchasesPage /></Suspense>} />
        {/* issue_04: Audit Trail page — ORGANIZATION_ADMIN only */}
        <Route path="/organization/audit-trail" element={
          <RoleGuard allowedRoles={[UserRole.ORGANIZATION_ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><AuditTrailPage /></Suspense>
          </RoleGuard>
        } />
        {/* issue_05: Compliance Dashboard page — ORGANIZATION_ADMIN only */}
        <Route path="/organization/compliance" element={
          <RoleGuard allowedRoles={[UserRole.ORGANIZATION_ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><ComplianceDashboardPage /></Suspense>
          </RoleGuard>
        } />
        {/* Redirect old URL to new one for backwards compatibility */}
        <Route path="/organization/purchases" element={<Navigate to="/organization/enrolls" replace />} />

        {/* ============================================================
            issue_217: Assessment routes — ORGANIZATION_ADMIN
            Static routes MUST come before parametric ones.
            - /organization/assessments/history — list past attempts
            - /organization/assessments/attempts/:attemptId/report — single report
            - /organization/assessments/:id/execute — execute an assessment
            - /organization/assessments — list available assessments
            - /organization/non-conformities — NC list and management
        ============================================================ */}
        <Route path="/organization/assessments/history" element={
          <RoleGuard allowedRoles={[UserRole.ORGANIZATION_ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><AssessmentReportPage /></Suspense>
          </RoleGuard>
        } />
        <Route path="/organization/assessments/attempts/:attemptId/report" element={
          <RoleGuard allowedRoles={[UserRole.ORGANIZATION_ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><AssessmentReportPage /></Suspense>
          </RoleGuard>
        } />
        <Route path="/organization/assessments/:id/execute" element={
          <RoleGuard allowedRoles={[UserRole.ORGANIZATION_ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><AssessmentExecutionPage /></Suspense>
          </RoleGuard>
        } />
        <Route path="/organization/assessments" element={
          <RoleGuard allowedRoles={[UserRole.ORGANIZATION_ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><AssessmentExecutionPage /></Suspense>
          </RoleGuard>
        } />
        <Route path="/organization/non-conformities" element={
          <RoleGuard allowedRoles={[UserRole.ORGANIZATION_ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><NonConformityListPage /></Suspense>
          </RoleGuard>
        } />

        {/* ============================================================
            issue_138: Organization member routes
            Role check is done within each page (ORGANIZATION_MEMBER).
            issue_191: Added CourseTestPage route — MUST be before
                       /my-courses/:deckListId to avoid route conflict.
        ============================================================ */}
        {/* issue_09: Stripe payment result pages */}
        <Route path="/courses/payment/success" element={<Suspense fallback={<PageLoader />}><StripeSuccessPage /></Suspense>} />
        <Route path="/courses/payment/cancel" element={<Suspense fallback={<PageLoader />}><StripeCancelPage /></Suspense>} />
        <Route path="/my-courses" element={<Suspense fallback={<PageLoader />}><MyCoursesPage /></Suspense>} />
        {/* issue_191: Course Test page — MUST be before /my-courses/:deckListId to avoid route conflict */}
        <Route path="/my-courses/:deckListId/test" element={
          <RoleGuard allowedRoles={[UserRole.ORGANIZATION_MEMBER, UserRole.ORGANIZATION_ADMIN, UserRole.CREATOR, UserRole.ADMIN]} fallback={<Navigate to="/onboarding" replace />}>
            <Suspense fallback={<PageLoader />}><CourseTestPage /></Suspense>
          </RoleGuard>
        } />
        <Route path="/my-courses/:deckListId" element={<Suspense fallback={<PageLoader />}><MyCourseDetailPage /></Suspense>} />
        <Route path="/my-progress" element={<Suspense fallback={<PageLoader />}><MyProgressPage /></Suspense>} />
      </Route>

      {/* Redirect /auth to callback */}
      <Route path="/auth" element={<Navigate to="/auth/callback" replace />} />

      {/* 404 */}
      <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>} />
    </Routes>
  );
}

export default AppRoutes;